import { prisma, Role, UserRoleAssignmentEntityType } from "client";

export const getRoles = async () => {
  const roles = await prisma.role.findMany();
  return roles;
};
export const getRolesPaginated = async (
  pageNumber: number,
  pageSize: number,
  searchByName?: string,
  lang?: string,
  orderBy?: {
    [key: string]: "asc" | "desc";
  }
) => {
  const skip = (pageNumber - 1) * pageSize;
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters

  const searchParam = searchByName ? escapeRegex(searchByName) : "";
  const filter = searchParam
    ? {
        OR: [{ name: { contains: searchParam, mode: "insensitive" } }],
      }
    : {};

  const roles = await prisma.role.findMany({
    // @ts-ignore
    where: filter,
    include: {
      permissions: {
        include: {
          permission: true,
        },
      },
    },
    skip,
    take: pageSize,
    orderBy,
  });
  const totalRoles = await prisma.role.count({
    // @ts-ignore
    where: filter,
  });
  return {
    data: roles,

    pagination: {
      pageNumber,
      pageSize,
      total: totalRoles,
      totalPages: Math.ceil(totalRoles / pageSize),
      hasNextPage: totalRoles > pageNumber * pageSize,
    },
  };
};

export const getRoleById = async (roleId: string) => {
  const role = await prisma.role.findFirst({
    where: {
      id: roleId,
    },
  });

  return role;
};

export const createRole = async (data: Role) => {
  const createdRole = await prisma.role.create({
    data: {
      ...data,
    },
  });

  return createdRole;
};

export const createRoleWithPermissions = async (
  role: Role,
  permissions: string[]
) => {
  const createdRole = await prisma.role.create({
    data: {
      ...role,
      permissions: {
        create: permissions.map((permission) => ({
          permission: {
            connect: {
              id: permission,
            },
          },
        })),
      },
    },
  });
  return createdRole;
};

export const updateRole = async (
  id: string,
  name: string,
  permissions: string[]
) => {
  const updatedRole = await prisma.role.update({
    where: {
      id,
    },
    data: {
      name,
      permissions: {
        deleteMany: {},
        create: permissions.map((permission) => ({
          permission: {
            connect: {
              id: permission,
            },
          },
        })),
      },
    },
  });

  return updatedRole;
};

export const deleteRole = async (id: string) => {
  try {
    // First delete all associated AssignmentModel records
    await prisma.assignmentModel.deleteMany({
      where: {
        roleId: id,
      },
    });

    await prisma.rolePermission.deleteMany({
      where: {
        roleId: id,
      },
    });

    await prisma.userRole.deleteMany({
      where: {
        roleId: id,
      },
    });

    // Then delete the role
    const deletedRole = await prisma.role.delete({
      where: {
        id,
      },
    });

    return { role: deletedRole };
  } catch (error) {
    return { error };
  }
};

export const assignRolesToUser = async ({
  userId,
  roleId,
  entityId,
  entityType,
}: {
  userId: string;
  roleId: string;
  entityId: string;
  entityType: UserRoleAssignmentEntityType;
}) => {
  const createdAssignment = await prisma.assignmentModel.create({
    data: {
      userId,
      roleId,
      entityId,
      entityType,
    },
  });

  return createdAssignment;
};

export const assignRolesToUserByRoleName = async ({
  userId,
  roleName,
  entityId,
  entityType,
}: {
  userId: string;
  roleName: string;
  entityId: string;
  entityType: UserRoleAssignmentEntityType;
}) => {
  const role = await prisma.role.findFirst({
    where: {
      name: roleName,
    },
  });
  if (!role) {
    return { error: "Role not found" };
  }
  const createdAssignment = await prisma.assignmentModel.create({
    data: {
      userId,
      roleId: role?.id,
      entityId,
      entityType,
    },
  });

  return createdAssignment;
};

export const hasOrganizationRole = async (
  userId: string,
  organizationId: string
) => {
  const organizationRole = await prisma.assignmentModel.findFirst({
    where: {
      userId,
      entityId: organizationId,
      entityType: UserRoleAssignmentEntityType.ORGANIZATION,
    },
  });
  return !!organizationRole;
};
